"use client";

import { ClientAvatarType } from "@prisma/client";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useFetchOfferAvatars } from "@/app/hooks/fetchs/client-avatar/useFetchOfferAvatars";
import { useFetchOfferAvatarsFreshness } from "@/app/hooks/fetchs/client-avatar/useFetchOfferAvatarsFreshness";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { Limits } from "@/app/vars/Limits";
import { getAvatar } from "./Avatars.logic";
import styles from "./Avatars.module.css";
import PrimaryCard from "./components/PrimaryCard";
import ProspectSearchCard from "./components/ProspectSearchCard/ProspectSearchCard";
import SmallCard from "./components/SmallCard";

type Props = DefaultProps & {
  params: { lang: string };
  offerId?: number;
  isAnalyzing?: boolean;
  onExtract?: (targetType: ClientAvatarType, isEnrich: boolean) => void;
  enabled?: boolean;
};

export default function Avatars(props: Props) {
  const { params, offerId, isAnalyzing = false, onExtract, enabled = true } = props;
  const { lang } = params;

  const user = useAuthUser();
  const { data: dbAvatars, isLoading } = useFetchOfferAvatars({
    offerId,
    enabled: enabled && !!user && !!offerId,
  });

  const { data: freshness } = useFetchOfferAvatarsFreshness({
    offerId,
    enabled: enabled && !!user && !!offerId,
  });

  const isTypeStale = (type: ClientAvatarType): boolean =>
    freshness?.avatars.find((a) => a.type === type)?.isStale ?? false;

  const primary = getAvatar(ClientAvatarType.PRIMARY, !!user, dbAvatars, offerId ?? -1);
  const others = [
    getAvatar(ClientAvatarType.SECONDARY, !!user, dbAvatars, offerId ?? -1),
    getAvatar(ClientAvatarType.EXPLORATORY, !!user, dbAvatars, offerId ?? -1),
  ];

  return (
    <div className={styles.container}>
      {isLoading ? (
        <div
          className={styles.mainLoaderContainer}
          style={{ display: "flex", justifyContent: "center", padding: "64px 0" }}
        >
          <PointsLoader
            centered
            delay={Limits.CLIENT_AVATAR_LOADER_DELAY}
            color="var(--color-green-3)"
          />
        </div>
      ) : (
        <div className={styles.grid}>
          {primary && (
            <PrimaryCard
              avatar={primary}
              lang={lang}
              isPlaceholder={!user}
              isAnalyzing={isAnalyzing}
              isStale={isTypeStale(primary.type)}
              onExtract={onExtract}
            />
          )}

          {others.length > 0 && (
            <div className={styles.smallCards}>
              {others.map((avatar) => (
                <SmallCard
                  key={avatar.type}
                  params={params}
                  avatar={avatar}
                  isPlaceholder={!user}
                  isAnalyzing={isAnalyzing}
                  isStale={isTypeStale(avatar.type)}
                  onExtract={onExtract}
                />
              ))}

              <ProspectSearchCard params={params} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
